import * as React from "react";
import "./App.css";
import "./reset.scss";

// Components
import { AdBanners } from "./components";
import { createGlobalStyle } from "styled-components";
import { BlockCollection } from "./components/block-collection";
import { MediaCollection } from "./components/media-collection";
import { PlaylistsCollection } from "./components/playlists-collection";
import { CembooThemeProvider } from "./libs/themes";

const GlobalStyles = createGlobalStyle`
    .cemboo-collection {
        --color-base-text: rgba(11,11,11, 1.0);

        --grid-container-columns: 1;
        --grid-container-gap: 24px;
        --grid-item--min-width: 250px;

        /* Calc some values */
        --grid-gap-count: calc(var(--grid-container-columns) - 1);
        --grid-gap-total-width: calc(var(--grid-gap-count) * var(--grid-container-gap));
        --grid-item--max-width: calc((100% - var(--grid-gap-total-width)) / var(--grid-container-columns));

        --video-grid-column-number: 12;
        --video-grid-column-gap: 24px;
        --video-grid-column-margin: 0;
        --video-grid-column-min-width: 240px;
        --video-grid-column-max-width: 1fr;

        --video-card-bg-color: rgba(11,11,11, 1.0);
        --video-card-text-color: rgba(255,255,255, 1.0);
        --video-card-text-color-alt: rgba(204,204,204, 1.0);
        --video-card-badge-bg-color: rgba(51,55,61, 1.0);
        --video-card-separator-color: rgba(119,119,119, 1.0);
        --video-card-shadow-color: rgba(0,5,13, 0.5);
        --video-card-poster-shadow-color: rgba(0,5,13, 0.28);
        --video-card-border-radius: 8px;
        --video-card-button-bg-color: rgba(184,249,79, 1.0);
        --video-card-button-label-color: rgba(11,11,11, 1.0);

        --viewer-rating-filled-color: rgba(184,249,79, 1.0);
        --viewer-rating-empty-color: rgba(119,119,119, 1.0);

        --video-screen-width: 100vw;

        --line-height-badge: 16px;
        --font-size-metadata: 16px;
        --line-height-metadata: 22px;

        --ease-out-back: cubic-bezier(0.34, 1.56, 0.64, 1);

        --amplify-components-flex-gap: 24px !important;

        @media (min-width: 576px) {
            --grid-container-columns: 1;
        }

        @media (min-width: 768px) {
            --grid-container-columns: 2;
        }

        @media (min-width: 992px) {
            --grid-container-columns: 3;
        }

        @media (min-width: 1200px) {
            --grid-container-columns: 4;
        }

        @media (min-width: 1400px) {
            --grid-container-columns: 5;
        }
    }
`;

export const App: React.FC<{
    clientId: string;
    outletId: string;
    fetchByDate: string;
    enableFilterControls: boolean;
    enableRatingControls: boolean;
    enableShareControls: boolean;
    enabled: boolean;
    mediaPerPage: number;
    showPlaylists: boolean;
    hideDefaultVOD: boolean;
    enableCompetitionMode: boolean;
    enableAutoplay: boolean;
    streamEnabled: boolean;
    streamTabControls: boolean;
    streamShareControls: boolean;
    disableFullscreenPlayer: boolean;
    previewMode: boolean;
    profileId?: string;
    versionId?: string;
    isolatePlaylistId?: string;
    theme?: "light" | "dark";
}> = ({
    clientId,
    outletId,
    fetchByDate,
    enableFilterControls,
    enableRatingControls,
    enableShareControls,
    enabled,
    mediaPerPage,
    showPlaylists,
    hideDefaultVOD,
    enableCompetitionMode,
    enableAutoplay,
    theme = "light",
    streamEnabled,
    streamTabControls,
    streamShareControls,
    disableFullscreenPlayer,
    previewMode,
    profileId,
    versionId,
    isolatePlaylistId,
}) => {
        const [isSignedIn, setIsSignedIn] = React.useState(false);
        const [sessionToken, setSessionToken] = React.useState<string>("");

        React.useEffect(() => {
            window.addEventListener(
                "authStatusUpdated",
                (event: CustomEventInit) => {
                    const sessionToken = event?.detail?.sessionToken;
                    const isSignedIn = event?.detail?.isSignedIn;
                    setIsSignedIn(isSignedIn);
                    setSessionToken(sessionToken);
                }
            );
        }, []);

        React.useEffect(() => {
            const liveStreamLibraryRenderedEvent = new CustomEvent(
                "liveStreamLibraryRendered"
            );

            window.dispatchEvent(liveStreamLibraryRenderedEvent);
        }, []);

        return (
            <div className="cemboo-collection" style={{ position: "relative" }}>
                <GlobalStyles></GlobalStyles>
                {enabled ? (
                    <>
                        {outletId !== "default" ? (
                            <BlockCollection
                                clientId={clientId}
                                outletId={outletId}
                                fetchByDate={fetchByDate}
                                enableRatingControls={enableRatingControls}
                                sessionToken={sessionToken}
                                isSignedIn={isSignedIn}
                                mediaPerPage={mediaPerPage}
                                enableCompetitionMode={enableCompetitionMode}
                                enableAutoplay={enableAutoplay}
                                disableFullscreenPlayer={disableFullscreenPlayer}
                                previewMode={previewMode}
                                profileId={profileId}
                                versionId={versionId}
                                themeMode={theme}
                            />
                        ) : (
                            <CembooThemeProvider mode={theme}>
                            <>
                                {!hideDefaultVOD && (
                                    <MediaCollection
                                        clientId={clientId}
                                        fetchByDate={fetchByDate}
                                        enableFilterControls={enableFilterControls}
                                        enableRatingControls={enableRatingControls}
                                        enableShareControls={enableShareControls}
                                        sessionToken={sessionToken}
                                        isSignedIn={isSignedIn}
                                        mediaPerPage={mediaPerPage}
                                        enableCompetitionMode={enableCompetitionMode}
                                        enableAutoplay={enableAutoplay}
                                    />
                                )}
                                {showPlaylists && (
                                    <PlaylistsCollection
                                        clientId={clientId}
                                        enableRatingControls={enableRatingControls}
                                        sessionToken={sessionToken}
                                        isSignedIn={isSignedIn}
                                        mediaPerPage={mediaPerPage}
                                        enableAutoplay={enableAutoplay}
                                        isolatePlaylistId={isolatePlaylistId}
                                    />
                                )}
                            </>
                            </CembooThemeProvider>
                        )}
                    </>
                ) : (
                    <div style={{ textAlign: "center" }}>
                        Media Library is disabled.
                    </div>
                )}

                <AdBanners clientId={clientId} />
            </div>
        );
    };
